import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "reducers";
import { stockService } from "../../services/stock.service";
import { IOutputValues } from "../../domain/IOutputValues";
import { IOutputOption } from "../../domain/IOutputOption";
import { IPartner } from "Dashboard/scenes/Partners/domain/IPartner";
import { IStockInput } from "../../domain/IStockInput";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IStockInputUpdate } from "../../domain/IStockInputUpdate";
import { getNextSortOrder, ISortParams, sort } from "utils/sortUtils";
import { IGetStockInputsRequestParams } from "../../domain/IGetStockInputsRequestParams";
import { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";
import { IStockReservation } from "../../domain/IStockReservation";
import { IStockReservationCreate } from "../../domain/IStockReservationCreate";
import { IStockReservationUpdate } from "../../domain/IStockReservationUpdate";

export const getStockInputs = createAsyncThunk(
  "Stock/getInputs", async (
    params: {
      getInputsRequestParams: IGetStockInputsRequestParams,
      pagingParams: IGetPageRequestParams,
      abortSignal: AbortSignal,
      updateStateAfterFetch: (
        updateStateParams: IUpdateStateAfterSearchParams
      ) => void
    },
    { rejectWithValue }
  ) => {
  try {
    const response = await stockService.getStockInputs(
      params.getInputsRequestParams,
      params.pagingParams,
      params.abortSignal);
    return {
      response: response,
      pagingData: params.pagingParams,
      updateStateAfterFetch: params.updateStateAfterFetch
    }
  } catch (err: any) {
    return rejectWithValue(err.response.status);
  }
}
);

export const updateStockInput = createAsyncThunk(
  "Stock/updateInput",
  async (data: IStockInputUpdate, { rejectWithValue }) => {
    try {
      return await stockService.updateStockInput(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteStockInput = createAsyncThunk(
  "Stock/deleteInput",
  async (inputId: number, { rejectWithValue }) => {
    try {
      const response = await stockService.deleteStockInput(inputId);
      return {
        response: response,
        inputId: inputId
      };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const getStockReservations = createAsyncThunk(
  "Stock/getReservations", async (
    params: any, { rejectWithValue }
  ) => {
  try {
    const response = await stockService.getReservations();
    return response;
  } catch (err: any) {
    return rejectWithValue(err.response.status);
  }
}
);

export const createStockReservation = createAsyncThunk(
  "Stock/createReservation", async (
    reservationRequest: IStockReservationCreate, { rejectWithValue }
  ) => {
  try {
    const response = await stockService.createReservation(reservationRequest);
    return response;
  } catch (err: any) {
    return rejectWithValue(err.response.status);
  }
}
);

export const updateStockReservation = createAsyncThunk(
  "Stock/updateReservation", async (
    reservationRequest: IStockReservationUpdate, { rejectWithValue }
  ) => {
  try {
    const response = await stockService.updateReservation(reservationRequest);
    return response;
  } catch (err: any) {
    return rejectWithValue(err.response.status);
  }
}
);

export const deleteStockReservation = createAsyncThunk(
  "Stock/deleteReservation", async (
    reservationId: number, { rejectWithValue }
  ) => {
  try {
    const response = await stockService.deleteReservation(reservationId);
    return {
      response: response,
      reservationId: reservationId
    };
  } catch (err: any) {
    return rejectWithValue(err.response.status);
  }
}
);


interface IUpdateInputItemPopoverState {
  isOpen: boolean;
  top?: number;
  left?: number;
  offsetTop?: number;
  offsetLeft?: number;
  itemRowIndex: number;
  focus?: string;
}

interface inputState {
  loading: boolean;
  reservations: IStockReservation[];
  inputs: IStockInput[];
  selectedInputId: number;
  selectedReservationId: number | undefined;
  deleteReservation: IStockReservation | undefined;
  sort: ISortParams | undefined;
  initialTotalValue: number;
  remainingTotalValue: number;
  totalQuantity: number;
  totalQuantityRemaining: number;
  outputValues: IOutputValues;
  error: {};
  partners: IPartner[];
  selectedPartner: IPartner | undefined;
  deleteInputModalOpen: boolean;
  indexForDeleteInput: number;
  updateInputItemPopover: IUpdateInputItemPopoverState;
  searchInputPopover: IPopoverState;
}

const initialState: inputState = {
  loading: false,
  reservations: [],
  inputs: [],
  selectedInputId: -1,
  selectedReservationId: undefined,
  deleteReservation: undefined,
  sort: undefined,
  error: {},
  initialTotalValue: 0,
  remainingTotalValue: 0,
  totalQuantity: 0,
  totalQuantityRemaining: 0,
  outputValues: {
    repository: undefined,
    map: {},
  },
  partners: [],
  selectedPartner: undefined,
  deleteInputModalOpen: false,
  indexForDeleteInput: -1,
  updateInputItemPopover: {
    isOpen: false,
    itemRowIndex: -1,
    top: 0,
    left: 0,
  },
  searchInputPopover: { isOpen: false },
};

export const inputSlice = createSlice({
  name: "input",
  initialState,
  reducers: {
    reset: (state) => {
      state = initialState;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setInputs: (state, action: PayloadAction<IStockInput[]>) => {
      state.inputs = action.payload;
    },
    setPartners: (state, action: PayloadAction<IPartner[]>) => {
      state.partners = action.payload;
    },
    setSort: (state, action: PayloadAction<ISortParams>) => {
      const newSortOrder = getNextSortOrder(state.sort, action.payload.key);
      const newSortParams = {
        key: action.payload.key,
        typeOfValue: action.payload.typeOfValue,
        order: newSortOrder
      };
      state.sort = newSortParams;
      state.inputs = sort(state.inputs, newSortParams);
    },
    setSelectedPartnerId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      if (action.payload) {
        let sp = state.partners.find((p) => p.partnerId === action.payload);
        state.selectedPartner = sp;
      } else {
        state.selectedPartner = undefined;
      }
    },
    setDeleteInputModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteInputModalOpen = action.payload.visible;
      state.indexForDeleteInput = action.payload.index;
    },
    setUpdateInputItemPopover: (
      state,
      action: PayloadAction<IUpdateInputItemPopoverState>
    ) => {
      state.updateInputItemPopover = action.payload;
    },
    addNewInput: (state, action: PayloadAction<IStockInput>) => {
      if (action.payload) {
        state.inputs = [action.payload, ...state.inputs];
        state.initialTotalValue += action.payload.initialTotalValue;
        state.remainingTotalValue += action.payload.remainingTotalValue;
        state.totalQuantity += action.payload.quantityAmountTotal;
        state.totalQuantityRemaining += action.payload.quantityAmountRemaining;
      }
    },
    setSearchInputPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchInputPopover = action.payload;
    },
    setOutputValue: (state, action: PayloadAction<IOutputOption>) => {
      const option = action.payload;
      if (option.value) {
        if (Object.keys(state.outputValues.map).length === 0) {
          state.outputValues.repository = option.repository;
          state.outputValues.map[option.key] = option.value;
        } else {
          if (
            state.outputValues.repository &&
            state.outputValues.repository.repositoryId ===
            option.repository.repositoryId
          ) {
            state.outputValues.map[option.key] = option.value;
          }
        }
      } else {
        delete state.outputValues.map[option.key];
        if (Object.keys(state.outputValues.map).length === 0)
          delete state.outputValues.repository;
      }
    },
    setSelectedInputId: (state, action: PayloadAction<number>) => {
      state.selectedInputId = action.payload;
    },
    setSelectedReservationId: (state, action: PayloadAction<number | undefined>) => {
      state.selectedReservationId = action.payload;
    },
    requestDeletingReservation: (state, action: PayloadAction<IStockReservation | undefined>) => {
      state.deleteReservation = action.payload;
    },
    resetOutputValue: (state) => {
      state.outputValues = {
        repository: undefined,
        map: {},
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStockInputs.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getStockInputs.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        if (action.payload.pagingData.fetchMode === 'replace') {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = action.payload.response.inputs;
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "replace",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else if (action.payload.pagingData.beforeId) {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = [...action.payload.response.inputs, ...state.inputs];
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "beforeFirst",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = [...state.inputs, ...action.payload.response.inputs];
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = action.payload.response.inputs?.[elementsFetched - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "afterLast",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        }
        state.initialTotalValue = action.payload.response.initialTotalValue;
        state.remainingTotalValue = action.payload.response.remainingTotalValue;
        state.totalQuantity = action.payload.response.totalQuantity;
        state.totalQuantityRemaining = action.payload.response.totalQuantityRemaining;
      }
    });
    builder.addCase(getStockReservations.fulfilled, (state, action) => {
      state.reservations = action.payload.reservations;
    });
    builder.addCase(createStockReservation.fulfilled, (state, action) => {
      state.reservations = [action.payload, ...state.reservations];
      action.payload.items.forEach(item => {
        state.inputs = state.inputs.map(input => input.inputId === item.input.inputId
          ? { ...item.input, quantityAmountRemaining: item.input.quantityAmountRemaining - item.quantityAmount }
          : input
        );
      });
    });
    builder.addCase(updateStockReservation.fulfilled, (state, action) => {
      state.reservations = state.reservations.map(reservation =>
        reservation.reservationId === action.payload.reservationId ? action.payload : reservation);
      action.payload.items.forEach(item => {
        state.inputs = state.inputs.map(input => input.inputId === item.input.inputId
          ? { ...item.input, quantityAmountRemaining: item.input.quantityAmountRemaining - item.quantityAmount }
          : input
        );
      });
    });
    builder.addCase(deleteStockReservation.fulfilled, (state, action) => {
      state.reservations = state.reservations.filter(reservation => {
        if (reservation.reservationId === action.payload.reservationId) {
          reservation.items.forEach(item => {
            state.inputs = state.inputs.map(input => input.inputId === item.input.inputId
              ? { ...input, quantityAmountRemaining: input.quantityAmountRemaining + item.quantityAmount }
              : input
            );
          });
        }
        return reservation.reservationId !== action.payload.reservationId;
      });
    });
    builder.addCase(updateStockInput.pending, (state) => {
    });
    builder.addCase(updateStockInput.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach(newInput => {
          state.inputs = state.inputs.map(i => {
            if (i.inputId === newInput.inputId) {
              if (i.initialTotalValue !== newInput.initialTotalValue) {
                state.initialTotalValue = state.initialTotalValue + newInput.initialTotalValue - i.initialTotalValue;
              }
              if (i.remainingTotalValue !== newInput.remainingTotalValue) {
                state.remainingTotalValue = state.remainingTotalValue + newInput.remainingTotalValue - i.remainingTotalValue;
              }
              if (i.quantityAmountRemaining !== newInput.quantityAmountRemaining) {
                state.totalQuantityRemaining = state.totalQuantityRemaining + newInput.quantityAmountRemaining - i.quantityAmountRemaining;
              }
              if (i.quantityAmountTotal !== newInput.quantityAmountTotal) {
                state.totalQuantity = state.totalQuantity + newInput.quantityAmountTotal - i.quantityAmountTotal;
              }
              return newInput;
            } else {
              return i;
            }
          });
          if (newInput.pricePerUnit === undefined || newInput.pricePerUnit === null || newInput.pricePerUnit === 0) {
            delete state.outputValues.map[newInput.inputId];
            if (Object.keys(state.outputValues.map).length === 0) {
              delete state.outputValues.repository;
            }
          }
        });
      }
    });
    builder.addCase(updateStockInput.rejected, (state, action) => {
      state.error = action.error;
    });
    builder.addCase(deleteStockInput.fulfilled, (state, action) => {
      if (action.payload) {
        const deletedInput = state.inputs.find(input => input.inputId === action.payload.inputId);
        if (deletedInput) {
          state.initialTotalValue = state.initialTotalValue - deletedInput.initialTotalValue;
          state.remainingTotalValue = state.remainingTotalValue - deletedInput.remainingTotalValue;
          state.totalQuantity = state.totalQuantity - deletedInput.quantityAmountTotal;
          state.totalQuantityRemaining = state.totalQuantityRemaining - deletedInput.quantityAmountRemaining;
        }
        state.inputs = state.inputs.filter(input => input.inputId !== action.payload.inputId);
      }
    });
  },
});

export const {
  setError,
  setInputs,
  setOutputValue,
  resetOutputValue,
  setSelectedInputId,
  setSelectedReservationId,
  requestDeletingReservation,
  setPartners,
  setSelectedPartnerId,
  setDeleteInputModalOpen,
  setUpdateInputItemPopover,
  addNewInput,
  setSearchInputPopover,
  setSort
} = inputSlice.actions;

export const selectState = (state: RootState) => state.stock.input;
export const selectStockInputForUpdateItem = (state: RootState) =>
  state.stock.input.inputs[state.stock.input.updateInputItemPopover.itemRowIndex];
export const selectStockInputForDelete = (state: RootState) =>
  state.stock.input.inputs[state.stock.input.indexForDeleteInput];

export default inputSlice.reducer;
